/**
 * Plate → HTML serializer for email / comment bodies.
 *
 * Uses Plate's NATIVE static serialization (`createStaticEditor` +
 * `serializeHtml` from `platejs/static`). A static editor is built whose
 * plugin set mirrors the interactive editor in
 * `hooks/usePlateEditorLogic.tsx` (same node types), but with lightweight
 * static React components that emit the exact semantic markup the email
 * backend / comment preview already understand.
 *
 * Three transforms wrap the native serializer:
 *   1. PRE-PASS — Plate's list model is indent-based (a list item is a <p>
 *      carrying listStyleType + indent, painted by components/BlockList).
 *      `reconstructLists` groups consecutive list <p> nodes and wraps them in
 *      real ul/ol/li element nodes so the static renderer emits proper
 *      <ul>/<ol><li> markup. Mirrors the ordered/bullet logic in BlockList
 *      and the list handling in exporters/docxExport.ts.
 *   2. IMAGE RESOLUTION — persisted image urls are docIds; resolved to https
 *      URLs via fetchImageUrlsBulk (overridable for tests) before rendering.
 *   3. POST-PASS — `cleanSerializedHtml` unwraps the slate-editor root, strips
 *      slate sentinel attributes/classes, collapses attribute-less wrapper
 *      <span>s, and removes zero-width chars — producing clean, portable HTML.
 *
 * CRITICAL: an empty document — `[{ type: 'p', children: [{ text: '' }] }]` —
 * serializes to '' (empty string) so the compose-email empty-body guard fires.
 */

import React from 'react';
import { createStaticEditor, serializeHtml, getStaticPlugins } from 'platejs/static';
import { BaseParagraphPlugin, createSlatePlugin } from 'platejs';
import {
    BaseBoldPlugin,
    BaseItalicPlugin,
    BaseUnderlinePlugin,
    BaseStrikethroughPlugin,
    BaseCodePlugin,
    BaseSubscriptPlugin,
    BaseSuperscriptPlugin,
    BaseH1Plugin,
    BaseH2Plugin,
    BaseH3Plugin,
    BaseBlockquotePlugin,
    BaseHorizontalRulePlugin,
} from '@platejs/basic-nodes';
import { BaseCodeBlockPlugin, BaseCodeLinePlugin } from '@platejs/code-block';
import { BaseFontColorPlugin, BaseFontBackgroundColorPlugin, BaseFontSizePlugin, BaseFontFamilyPlugin } from '@platejs/basic-styles';
import { BaseLinkPlugin } from '@platejs/link';
import { BaseMentionPlugin } from '@platejs/mention';
import { BaseTablePlugin, BaseTableRowPlugin, BaseTableCellPlugin, BaseTableCellHeaderPlugin } from '@platejs/table';
import type { Value } from 'platejs';

import { fetchImageUrlsBulk, isDocId } from '../services/imageUploadService';
import { computeOrderedStartFromSiblings } from '../components/BlockList/BlockList';
import { PlateNode, PlateToHtmlOptions } from './plateToHtmlTypes';
import {
    PLATE_NODE_TYPES,
    LIST_NODE_TYPES,
    ORDERED_LIST_STYLES,
    MENTION_CLASS,
    MENTION_DATA_ATTR,
    SLATE_EDITOR_ROOT_CLASS,
    SLATE_DATA_ATTR_PREFIX,
    SLATE_CLASS_PREFIX,
    SLATE_RELATIVE_STYLE,
    ZERO_WIDTH_CHARS_REGEX,
} from './plateToHtmlConstants';

// ─────────────────────────────────────────────────────────────────────────────
// Static node components — minimal semantic tags, no editor chrome.
// ─────────────────────────────────────────────────────────────────────────────

// Email static-component colors — mirror PlateEditor.module.scss.
const EMAIL_CELL_BORDER_COLOR = '#d8d6d2';
const EMAIL_CAPTION_COLOR = '#6b6b6b';

const wrapTag =
    (tag: keyof JSX.IntrinsicElements) =>
    (props: any): React.ReactElement =>
        React.createElement(tag, undefined, props.children);

// A merged cell carries its span on the node — @platejs/table stores it as
// colSpan/rowSpan (camelCase) or under attributes.colspan/rowspan (its own
// getColSpan reads `cell.colSpan ?? cell.attributes?.colspan`). Read both and
// emit only when > 1, so a normal (unmerged) cell stays a byte-identical bare
// <td>/<th>. Span-ness is CONTENT, not email chrome, so it is forwarded in
// EVERY surface (email + comments + PDF) — unlike the cell cosmetics below.
const toSpan = (v: unknown): number | undefined => {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? parseInt(v, 10) : NaN;
    return Number.isFinite(n) && n > 1 ? n : undefined;
};

// Like wrapTag but forwards colSpan/rowSpan, and optionally stamps email inline
// styles on td/th cells. The email style values mirror PlateEditor.module.scss
// td/th: border 1px solid #d8d6d2, padding 10px 12px, vertical-align top. Cell
// cosmetics are only applied when emailStyles mode is active; otherwise bare
// <td>/<th> so class-based CSS rules take effect. Also reads element.background /
// element.backgroundColor for cell fill, and adds lineHeight:'1.5' to match the
// editor's td/th rendering.
//
// emailStyles is threaded in via the factory function so each plugin instance
// is built once per plateToHtml call (buildStaticPlugins receives the flag).
const wrapStyledTag =
    (tag: 'td' | 'th', emailStyles: boolean) =>
    (props: any): React.ReactElement => {
        const el = props.element ?? {};
        const colSpan = toSpan(el.colSpan ?? el.attributes?.colspan);
        const rowSpan = toSpan(el.rowSpan ?? el.attributes?.rowspan);
        const spanProps = colSpan || rowSpan ? { ...(colSpan ? { colSpan } : {}), ...(rowSpan ? { rowSpan } : {}) } : undefined;
        if (!emailStyles) return React.createElement(tag, spanProps, props.children);
        const bg: string | undefined = el.background ?? el.backgroundColor;
        const cellStyle: React.CSSProperties = {
            height: '44px',
            border: `1px solid ${EMAIL_CELL_BORDER_COLOR}`,
            padding: '10px 12px',
            verticalAlign: 'top',
            // Left-align to match the editor; without this <th> falls back to the
            // browser default (centered) and headers drift vs the editor preview.
            // A paragraph-level align still overrides this for its own content.
            textAlign: 'left',
            lineHeight: '1.5',
            ...(bg ? { backgroundColor: bg } : {}),
        };
        return React.createElement(tag, { ...spanProps, style: cellStyle }, props.children);
    };

// Color / highlight / font-size / font-family leaf marks. The Base*Plugin keys
// (KEYS.color, KEYS.backgroundColor, KEYS.fontSize, KEYS.fontFamily) match the
// CSS property names exactly, so the leaf is stored under the same key we emit
// as inline style (e.g. { text:'x', color:'#ff0000' } → <span style="color:#ff0000">).
// These are CONTENT, not email chrome, so they render in every surface (email +
// comments). fontSize may be stored as a bare number; append 'px' in that case.
const makeStyledMark =
    (cssProp: 'color' | 'backgroundColor' | 'fontSize' | 'fontFamily') =>
    (props: any): React.ReactElement => {
        const v = props.leaf?.[cssProp];
        if (v == null || v === '') return React.createElement(React.Fragment, null, props.children);
        const cssValue = cssProp === 'fontSize' && typeof v === 'number' ? `${v}px` : String(v);
        return React.createElement('span', { style: { [cssProp]: cssValue } }, props.children);
    };

// Paragraph — emits text-align inline whenever the node carries a non-default
// `align` (set by the toolbar AlignMenu or carried from a pasted spreadsheet/
// document cell). Alignment is CONTENT, not email chrome, so it is preserved in
// EVERY serialization surface (email AND comments) — unlike the email-only
// table/list cosmetics. Inline so it survives email clients (which strip <style>).
const PARAGRAPH_ALIGNS = new Set(['right', 'center', 'justify']);
const ParagraphStatic = (props: any): React.ReactElement => {
    const align = props.element?.align;
    if (typeof align === 'string' && PARAGRAPH_ALIGNS.has(align)) {
        return React.createElement('p', { style: { textAlign: align as React.CSSProperties['textAlign'] } }, props.children);
    }
    return React.createElement('p', undefined, props.children);
};

// Email/preview link styling — inline (email clients strip <style> blocks) so
// links read as links: blue + underline.
const LINK_STYLE: React.CSSProperties = { color: '#2563eb', textDecoration: 'underline' };

// A bare domain ("google.com") serialized as href="google.com" resolves
// relative to the current page (→ localhost:3337/google.com). Prepend https://
// unless the url already has a scheme / is protocol-relative / an anchor.
const normalizeHref = (raw: string): string => {
    const url = (raw ?? '').trim();
    if (!url) return '';
    if (/^(https?:|mailto:|tel:|#|\/\/)/i.test(url)) return url;
    return `https://${url}`;
};

const LinkStatic = (props: any): React.ReactElement =>
    React.createElement(
        'a',
        { href: normalizeHref(props.element?.url ?? ''), target: '_blank', rel: 'noopener noreferrer', style: LINK_STYLE },
        props.children,
    );

// Mention chip — markup mirrors the legacy slateToHtml output the email
// backend/preview parse: <span class="text-editor-mention"
// data-mention-user-id="{userId}">@Name</span>. The Plate mention shape is
// { type:'mention', key:userId, value:'@Name' }; legacy migrated mentions may
// carry `userId` instead of `key`.
const MentionStatic = (props: any): React.ReactElement => {
    const el = props.element ?? {};
    const userId = String(el.key ?? el.userId ?? '');
    const label = typeof el.value === 'string' ? el.value : '';
    return React.createElement('span', { className: MENTION_CLASS, [MENTION_DATA_ATTR]: userId }, label);
};

// Block image — persisted url is a docId, resolved to https before render
// (see resolveImageUrls). Void node, so children are ignored.
// Inline styles carry the stored width (so pasted/resized images honour the
// author's intent in email clients) and cap to 100 % so unset images never
// overflow a narrow viewport. Email clients strip <style> blocks, so these
// must be inline.
// When element.caption is a non-empty string, wraps in <figure>/<figcaption>;
// otherwise emits a bare <img> (byte-identical to the pre-caption output).
const ImageStatic = (props: any): React.ReactElement | null => {
    const url = props.element?.url;
    if (url == null || url === '') return null;
    const storedWidth: number | undefined = typeof props.element?.width === 'number' ? props.element.width : undefined;
    const imgStyle: React.CSSProperties = {
        maxWidth: '100%',
        height: 'auto',
        ...(storedWidth != null ? { width: `${storedWidth}px` } : {}),
    };
    const img = React.createElement('img', { src: String(url), alt: props.element?.alt ?? '', style: imgStyle });
    const caption = typeof props.element?.caption === 'string' ? props.element.caption.trim() : '';
    if (!caption) return img;
    // Constrain the figure to the image width and kill the browser's default
    // 40px figure margin; style the caption to match the editor's read-only
    // .caption (ImageElement.module.scss): centered, 12px, muted grey.
    const figureStyle: React.CSSProperties = {
        margin: 0,
        display: 'inline-block',
        ...(storedWidth != null ? { width: `${storedWidth}px` } : {}),
    };
    const figcaptionStyle: React.CSSProperties = {
        marginTop: '4px',
        padding: '2px 4px',
        fontSize: '12px',
        color: EMAIL_CAPTION_COLOR,
        textAlign: 'center',
        lineHeight: '1.4',
    };
    return React.createElement(
        'figure',
        { style: figureStyle },
        img,
        React.createElement('figcaption', { style: figcaptionStyle }, caption),
    );
};

const HrStatic = (): React.ReactElement => React.createElement('hr');

// Code block — emits <pre><code class="language-xxx">…</code></pre>.
// Children are code_line elements rendered by CodeLineStatic below.
const CodeBlockStatic = (props: any): React.ReactElement => {
    const lang: string = props.element?.lang ?? 'plaintext';
    return React.createElement('pre', undefined, React.createElement('code', { className: `language-${lang}` }, props.children));
};

// Code line — a single line inside a code block. The trailing "\n" is what
// actually separates lines inside <pre>: sibling inline spans with no newline
// would render as one mashed line (and break multi-line code in email/PDF).
const CodeLineStatic = (props: any): React.ReactElement => React.createElement('span', undefined, props.children, '\n');

// Table — wrap rows in <table><tbody> so the markup is valid HTML the email
// renderer can lay out. Row/cell children render via their own components.
// border-collapse:collapse inline so email clients (which strip <style> blocks)
// render a proper bordered grid instead of the double-border default.
// If the node carries colSizes, emit a <colgroup> with explicit <col> widths so
// column proportions match the editor (mirrors tableElements.tsx:86-91).
// border-collapse + colgroup are emailStyles-only so the comment/PDF path stays a
// bare <table><tbody> (byte-identical to base; class CSS keeps full control there).
const TABLE_STYLE: React.CSSProperties = { borderCollapse: 'collapse' };
const makeTableStatic =
    (emailStyles: boolean) =>
    (props: any): React.ReactElement => {
        if (!emailStyles) {
            return React.createElement('table', undefined, React.createElement('tbody', undefined, props.children));
        }
        const colSizes: number[] | undefined = Array.isArray(props.element?.colSizes) ? props.element.colSizes : undefined;
        const hasColSizes = colSizes != null && colSizes.length > 0;
        const colgroup = hasColSizes
            ? React.createElement(
                  'colgroup',
                  undefined,
                  ...colSizes!.map((w, i) =>
                      typeof w === 'number' && w > 0
                          ? React.createElement('col', { key: i, style: { width: `${w}px` } })
                          : React.createElement('col', { key: i }),
                  ),
              )
            : null;
        // With explicit colSizes, render the table at the editor's actual column
        // widths (sum of cols) and lock the layout so cells don't get stretched
        // to the container. Without colSizes, keep just border-collapse.
        const sum = hasColSizes ? colSizes!.reduce((a, b) => a + (typeof b === 'number' && b > 0 ? b : 0), 0) : 0;
        const tableStyle: React.CSSProperties = hasColSizes
            ? { borderCollapse: 'collapse', tableLayout: 'fixed', width: `${sum}px`, maxWidth: '100%' }
            : TABLE_STYLE;
        return React.createElement('table', { style: tableStyle }, colgroup, React.createElement('tbody', undefined, props.children));
    };

// ─────────────────────────────────────────────────────────────────────────────
// Static plugin set — mirrors usePlateEditorLogic.tsx node-type coverage.
// ─────────────────────────────────────────────────────────────────────────────

const ImageStaticPlugin = createSlatePlugin({
    key: PLATE_NODE_TYPES.IMAGE,
    node: { isElement: true, isVoid: true, type: PLATE_NODE_TYPES.IMAGE },
}).withComponent(ImageStatic);

// Marker cycling — mirrors BlockList.styleByIndent so the preview's nested
// markers match the editor exactly: bullets cycle disc → circle → square,
// numbers cycle decimal → lower-alpha → lower-roman by 1-based indent depth.
// The stored listStyleType is the BASE; the cycled value is computed here and
// stamped on the list node as `cssListStyleType` for the components below.
const BULLET_CYCLE = ['disc', 'circle', 'square'];
const NUMBERED_CYCLE = ['decimal', 'lower-alpha', 'lower-roman'];
function listStyleByIndent(base: string | undefined, indent: number | undefined): string | undefined {
    if (!base) return undefined;
    const depth = Math.max(0, (indent ?? 1) - 1);
    if (BULLET_CYCLE.includes(base)) return BULLET_CYCLE[depth % BULLET_CYCLE.length];
    if (NUMBERED_CYCLE.includes(base)) return NUMBERED_CYCLE[depth % NUMBERED_CYCLE.length];
    return base;
}

// Synthetic list wrappers injected by reconstructLists. The ul/ol components
// emit the cycled `list-style-type` (and ol `start`) stamped on the node.
//
// When emailStyles is true: inline margin:0 + padding-left:24px so email
// clients (which strip <style> blocks) show markers. When false: no inline
// styles so the .comment-render / PlateEditor class-based rules take effect.
const makeListWrap =
    (tag: 'ul' | 'ol', emailStyles: boolean) =>
    (props: any): React.ReactElement => {
        const el = props.element ?? {};
        const listStyleType = el.cssListStyleType ? { listStyleType: el.cssListStyleType as string } : {};
        const style: React.CSSProperties = emailStyles ? { margin: 0, paddingLeft: '24px', ...listStyleType } : { ...listStyleType };
        const extra = tag === 'ol' && typeof el.listStart === 'number' ? { start: el.listStart } : {};
        const styleProps = Object.keys(style).length > 0 ? { style } : {};
        return React.createElement(tag, { ...styleProps, ...extra }, props.children);
    };

// When emailStyles is true: inline margin:0 mirrors PlateEditor.module.scss li
// rule. When false: no inline styles.
const makeLiWrap =
    (emailStyles: boolean) =>
    (props: any): React.ReactElement => {
        const el = props.element ?? {};
        const listStyleType = el.cssListStyleType ? { listStyleType: el.cssListStyleType as string } : {};
        const style: React.CSSProperties = emailStyles ? { margin: 0, ...listStyleType } : { ...listStyleType };
        const styleProps = Object.keys(style).length > 0 ? { style } : {};
        return React.createElement('li', { ...styleProps }, props.children);
    };

// Callout has no dedicated email markup — render as a blockquote.
const CalloutStaticPlugin = createSlatePlugin({
    key: PLATE_NODE_TYPES.CALLOUT,
    node: { isElement: true, type: PLATE_NODE_TYPES.CALLOUT },
}).withComponent(wrapTag('blockquote'));

// Column layout has no email equivalent — flatten to plain block wrappers so
// children still render in document order.
const ColumnGroupStaticPlugin = createSlatePlugin({
    key: PLATE_NODE_TYPES.COLUMN_GROUP,
    node: { isElement: true, type: PLATE_NODE_TYPES.COLUMN_GROUP },
}).withComponent(wrapTag('div'));
const ColumnStaticPlugin = createSlatePlugin({
    key: PLATE_NODE_TYPES.COLUMN,
    node: { isElement: true, type: PLATE_NODE_TYPES.COLUMN },
}).withComponent(wrapTag('div'));

// TOC is a derived, interactive block. Its content is built at runtime from
// the live heading list — there is nothing meaningful to serialize. Render
// nothing in email/PDF exports. Existing documents WITHOUT a toc node are
// byte-identical (this plugin only fires for nodes with type='toc').
const TocStaticPlugin = createSlatePlugin({
    key: PLATE_NODE_TYPES.TOC,
    node: { isElement: true, isVoid: true, type: PLATE_NODE_TYPES.TOC },
}).withComponent((): React.ReactElement | null => null);

function buildStaticPlugins(emailStyles: boolean) {
    return [
        ...getStaticPlugins(),
        // Marks
        (BaseBoldPlugin as any).withComponent(wrapTag('strong')),
        (BaseItalicPlugin as any).withComponent(wrapTag('em')),
        (BaseUnderlinePlugin as any).withComponent(wrapTag('u')),
        (BaseStrikethroughPlugin as any).withComponent(wrapTag('s')),
        (BaseCodePlugin as any).withComponent(wrapTag('code')),
        (BaseSubscriptPlugin as any).withComponent(wrapTag('sub')),
        (BaseSuperscriptPlugin as any).withComponent(wrapTag('sup')),
        // Color / highlight / font-size / font-family — always-on (content, not email chrome).
        (BaseFontColorPlugin as any).withComponent(makeStyledMark('color')),
        (BaseFontBackgroundColorPlugin as any).withComponent(makeStyledMark('backgroundColor')),
        (BaseFontSizePlugin as any).withComponent(makeStyledMark('fontSize')),
        (BaseFontFamilyPlugin as any).withComponent(makeStyledMark('fontFamily')),
        // Blocks
        (BaseParagraphPlugin as any).withComponent(ParagraphStatic),
        (BaseH1Plugin as any).withComponent(wrapTag('h1')),
        (BaseH2Plugin as any).withComponent(wrapTag('h2')),
        (BaseH3Plugin as any).withComponent(wrapTag('h3')),
        (BaseBlockquotePlugin as any).withComponent(wrapTag('blockquote')),
        (BaseHorizontalRulePlugin as any).withComponent(HrStatic),
        // Inline
        (BaseLinkPlugin as any).withComponent(LinkStatic),
        (BaseMentionPlugin as any).withComponent(MentionStatic),
        // Tables — the base plugins only register node types; without explicit
        // static components PlateStatic falls back to <div>. Wrap each in its
        // real HTML tag (mirrors docxExport's table/tr/td/th coverage).
        // td/th carry inline border + padding only in emailStyles mode so email
        // clients (which strip <style> blocks) render a properly bordered grid.
        (BaseTablePlugin as any).withComponent(makeTableStatic(emailStyles)),
        (BaseTableRowPlugin as any).withComponent(wrapTag('tr')),
        (BaseTableCellPlugin as any).withComponent(wrapStyledTag('td', emailStyles)),
        (BaseTableCellHeaderPlugin as any).withComponent(wrapStyledTag('th', emailStyles)),
        // Image + layout + synthetic lists (list components are emailStyles-aware)
        ImageStaticPlugin,
        CalloutStaticPlugin,
        ColumnGroupStaticPlugin,
        ColumnStaticPlugin,
        TocStaticPlugin,
        createSlatePlugin({ key: LIST_NODE_TYPES.UL, node: { isElement: true, type: LIST_NODE_TYPES.UL } }).withComponent(
            makeListWrap('ul', emailStyles),
        ),
        createSlatePlugin({ key: LIST_NODE_TYPES.OL, node: { isElement: true, type: LIST_NODE_TYPES.OL } }).withComponent(
            makeListWrap('ol', emailStyles),
        ),
        createSlatePlugin({ key: LIST_NODE_TYPES.LI, node: { isElement: true, type: LIST_NODE_TYPES.LI } }).withComponent(
            makeLiWrap(emailStyles),
        ),
        // Code block: <pre><code class="language-xxx">…</code></pre>
        (BaseCodeBlockPlugin as any).withComponent(CodeBlockStatic),
        (BaseCodeLinePlugin as any).withComponent(CodeLineStatic),
    ];
}

// ─────────────────────────────────────────────────────────────────────────────
// Pre-pass: reconstruct indent-based lists into ul/ol/li element nodes.
// ─────────────────────────────────────────────────────────────────────────────

function isOrderedList(listStyleType: string | undefined): boolean {
    return ORDERED_LIST_STYLES.includes(listStyleType ?? '');
}

// A list item is any block <p> carrying a listStyleType. We only treat <p>
// as list items (matches LIST_INDENT_TARGETS minus headings/blockquote —
// those keep their own tag even when they carry list props, which is rare).
function isListItem(node: PlateNode): boolean {
    return node?.type === PLATE_NODE_TYPES.PARAGRAPH && node?.listStyleType != null;
}

// Strip list props off a node so the inner <p> doesn't carry them into the
// serialized markup (the surrounding <li> owns the list semantics).
function stripListProps(node: PlateNode): PlateNode {
    const { listStyleType, listStart, listRestart, indent, ...rest } = node;
    return rest as PlateNode;
}

// Positional <ol start> for a list item that arrived with no explicit
// `listStart` (the common case — see BlockList.tsx's own comment: "arrives
// undefined on freshly-loaded templates"). Mirrors computeOrderedStart's
// EXACT semantics via the shared pure walk so the exported <ol start> matches
// what the editor would render for this item — `documentNodes` is the FLAT
// array the item actually lives in (before list-reconstruction groups it into
// a run), matching the "siblings" the editor's own findPath-based lookup
// would resolve to.
function positionalListStart(item: PlateNode, documentNodes: PlateNode[]): number | undefined {
    const idx = documentNodes.indexOf(item);
    if (idx === -1) return undefined;
    return computeOrderedStartFromSiblings(documentNodes, idx, item.listStyleType as string, item.indent);
}

// Group consecutive list items into a single ul/ol containing one li per
// item. Nesting by `indent` is flattened to a single level — email clients
// render deep indent inconsistently, and the docx path also renders by
// numbering level rather than literal nesting.
function wrapListRun(items: PlateNode[], documentNodes: PlateNode[]): PlateNode {
    const ordered = isOrderedList(items[0]?.listStyleType);
    const listStart = items[0]?.listStart ?? (ordered ? positionalListStart(items[0], documentNodes) : undefined);
    // Flat mode keeps mixed indent depths in one list, so the cycled marker is
    // stamped per-<li> (from each item's own depth) rather than on the wrapper.
    const liNodes: PlateNode[] = items.map((item) => ({
        type: LIST_NODE_TYPES.LI,
        children: [stripListProps(item)],
        cssListStyleType: listStyleByIndent(item.listStyleType, item.indent),
    }));
    const wrapper: PlateNode = {
        type: ordered ? LIST_NODE_TYPES.OL : LIST_NODE_TYPES.UL,
        children: liNodes,
    };
    if (ordered && typeof listStart === 'number') wrapper.listStart = listStart;
    return wrapper;
}

// Nested variant: build real ul/ol trees from each item's `indent` depth (1 =
// top level). A deeper item nests inside the previous li; a shallower item pops
// back up; an ordered/bulleted flip at the same depth starts a sibling list.
// Returns the top-level list nodes produced from the run (usually one, but a
// top-level direction flip yields more).
function wrapListRunNested(items: PlateNode[], documentNodes: PlateNode[]): PlateNode[] {
    const roots: PlateNode[] = [];
    // Each stack frame is the currently-open list at a given depth.
    const stack: { list: PlateNode; depth: number }[] = [];

    const makeList = (item: PlateNode): PlateNode => {
        const ordered = isOrderedList(item.listStyleType);
        const list: PlateNode = {
            type: ordered ? LIST_NODE_TYPES.OL : LIST_NODE_TYPES.UL,
            children: [],
            // All items in a nested list share one depth → one cycled marker.
            cssListStyleType: listStyleByIndent(item.listStyleType, item.indent),
        };
        // Each new nested list corresponds 1:1 to a real indent-level <ol> in
        // the editor, so its start uses the SAME positional derivation as the
        // flat-mode wrapper above — matching computeOrderedStart for this item.
        const nestedListStart = item.listStart ?? (ordered ? positionalListStart(item, documentNodes) : undefined);
        if (ordered && typeof nestedListStart === 'number') list.listStart = nestedListStart;
        return list;
    };

    for (const item of items) {
        const depth = Math.max(1, item.indent ?? 1);
        const li: PlateNode = { type: LIST_NODE_TYPES.LI, children: [stripListProps(item)] };

        // Pop any open lists deeper than this item.
        while (stack.length && stack[stack.length - 1].depth > depth) stack.pop();

        const top = stack[stack.length - 1];
        const ordered = isOrderedList(item.listStyleType);
        const topOrdered = top && top.list.type === LIST_NODE_TYPES.OL;

        // A `listRestart` starts a new counter: fall through to the sibling-list
        // logic below so only frames at this depth and deeper close — shallower
        // frames stay open, keeping a restarted sublist under its outer <li>
        // and the outer list's own numbering intact.
        if (top && top.depth === depth && topOrdered === ordered && !item.listRestart) {
            // Same level, same direction → another <li> in the open list.
            top.list.children!.push(li);
            continue;
        }

        // Need a new list. Attach it to the last <li> of the parent list (nest)
        // or to the document root (top level / sibling after a direction flip
        // or a listRestart).
        const newList = makeList(item);
        newList.children!.push(li);
        if (top && top.depth < depth) {
            const parentItems = top.list.children!;
            const parentLi = parentItems[parentItems.length - 1];
            (parentLi.children ||= []).push(newList);
            stack.push({ list: newList, depth });
        } else {
            // top.depth === depth with a direction flip → sibling list. Replace
            // the same-depth frame; otherwise it's a fresh top-level list.
            if (top && top.depth === depth) stack.pop();
            const parent = stack[stack.length - 1];
            if (parent) {
                const parentItems = parent.list.children!;
                const parentLi = parentItems[parentItems.length - 1];
                (parentLi.children ||= []).push(newList);
            } else {
                roots.push(newList);
            }
            stack.push({ list: newList, depth });
        }
    }

    return roots;
}

function reconstructLists(nodes: PlateNode[], nestLists = false): PlateNode[] {
    const out: PlateNode[] = [];
    let run: PlateNode[] = [];

    const flush = () => {
        if (!run.length) return;
        if (nestLists) out.push(...wrapListRunNested(run, nodes));
        else out.push(wrapListRun(run, nodes));
        run = [];
    };

    for (const node of nodes) {
        if (isListItem(node)) {
            // Flat mode splits runs when the list direction flips (each becomes a
            // separate sibling list). Nested mode keeps the whole run together —
            // wrapListRunNested structures depth + direction flips itself.
            if (!nestLists && run.length && isOrderedList(run[0].listStyleType) !== isOrderedList(node.listStyleType)) {
                flush();
            }
            // A `listRestart` marks the START of a new Word counter (RECUR-27303
            // §2.9). Flat mode shares one <ol> per run, so the run must SPLIT
            // here for the restart to land as the next run's items[0] — the only
            // position wrapListRun reads a `start` from. Without this, a mid-run
            // restart (two adjacent Word numIds with no intervening block) is
            // unrepresentable: the whole run would number straight through.
            // Nested mode instead handles the restart inside wrapListRunNested's
            // stack walk — a full flush here would evict a deeper restarted
            // sublist from its outer <li> and split the outer list. Unreachable
            // on any existing document (nothing produces listRestart today).
            if (!nestLists && run.length && node.listRestart) {
                flush();
            }
            run.push(node);
            continue;
        }
        flush();
        // Recurse into container children (tables, columns) so nested lists
        // also reconstruct.
        if (Array.isArray(node.children) && node.type !== PLATE_NODE_TYPES.MENTION) {
            out.push({ ...node, children: reconstructLists(node.children, nestLists) });
        } else {
            out.push(node);
        }
    }
    flush();
    return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Image resolution: docId → https URL.
// ─────────────────────────────────────────────────────────────────────────────

function collectImageDocIds(nodes: PlateNode[]): string[] {
    const ids = new Set<string>();
    const stack: PlateNode[] = [...nodes];
    while (stack.length) {
        const node = stack.pop();
        if (!node) continue;
        if (node.type === PLATE_NODE_TYPES.IMAGE && node.url != null && isDocId(node.url)) {
            ids.add(String(node.url));
        }
        if (Array.isArray(node.children)) stack.push(...node.children);
    }
    return [...ids];
}

function resolveImageNodes(nodes: PlateNode[], urlMap: Record<string, string>): PlateNode[] {
    return nodes.map((node) => {
        const next: PlateNode = { ...node };
        if (next.type === PLATE_NODE_TYPES.IMAGE && next.url != null && isDocId(next.url)) {
            next.url = urlMap[String(next.url)] ?? next.url;
        }
        if (Array.isArray(next.children)) {
            next.children = resolveImageNodes(next.children, urlMap);
        }
        return next;
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Emptiness check — drives the empty-string contract.
// ─────────────────────────────────────────────────────────────────────────────

// A document is "empty" when it has no rendered text and no void/media nodes
// (image, hr, table). Whitespace-only text counts as empty.
export function isEmptyDocument(nodes: PlateNode[]): boolean {
    const VOID_TYPES = new Set<string>([PLATE_NODE_TYPES.IMAGE, PLATE_NODE_TYPES.HR, PLATE_NODE_TYPES.TABLE, PLATE_NODE_TYPES.MENTION]);
    let hasContent = false;
    const visit = (node: PlateNode | undefined) => {
        if (hasContent || !node) return;
        if (node.type && VOID_TYPES.has(node.type)) {
            hasContent = true;
            return;
        }
        if (typeof node.text === 'string' && node.text.trim() !== '') {
            hasContent = true;
            return;
        }
        if (Array.isArray(node.children)) node.children.forEach(visit);
    };
    nodes.forEach(visit);
    return !hasContent;
}

// ─────────────────────────────────────────────────────────────────────────────
// Post-pass: scrub serializeHtml's wrapper noise into clean semantic HTML.
// ─────────────────────────────────────────────────────────────────────────────

function cleanSerializedHtml(html: string, emailStyles: boolean): string {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const root = parsed.querySelector(`.${SLATE_EDITOR_ROOT_CLASS}`) ?? parsed.body;

    // Strip slate sentinel attributes, slate-* classes, and the
    // position:relative wrapper style off every element.
    root.querySelectorAll('*').forEach((el) => {
        Array.from(el.attributes).forEach((attr) => {
            if (attr.name.startsWith(SLATE_DATA_ATTR_PREFIX)) el.removeAttribute(attr.name);
        });
        if (el.classList.length) {
            const kept = Array.from(el.classList).filter(
                (c) => !c.startsWith(SLATE_CLASS_PREFIX) && c !== `${SLATE_CLASS_PREFIX.slice(0, -1)}`,
            );
            if (kept.length) el.setAttribute('class', kept.join(' '));
            else el.removeAttribute('class');
        }
        const style = el.getAttribute('style');
        if (style && style.replace(/\s/g, '') === SLATE_RELATIVE_STYLE) el.removeAttribute('style');
    });

    // Collapse attribute-less wrapper <span>s (serializeHtml nests several per
    // leaf). Repeat until stable since they nest.
    let collapsed = true;
    while (collapsed) {
        collapsed = false;
        root.querySelectorAll('span').forEach((span) => {
            if (span.attributes.length === 0) {
                span.replaceWith(...Array.from(span.childNodes));
                collapsed = true;
            }
        });
    }

    // Email-only: stamp margin:0 inline on any <p> that is a direct child of
    // <li>. This mirrors PlateEditor.module.scss `li p { margin: 0 }` so list
    // items don't get double vertical spacing in email clients (which strip
    // <style> blocks). Skipped for comments/PDF paths — their class rules handle it.
    if (emailStyles) {
        root.querySelectorAll('li > p').forEach((p) => {
            const existing = (p as HTMLElement).style.cssText;
            if (!existing.includes('margin')) {
                (p as HTMLElement).style.margin = '0';
            }
        });

        // Email-only: zero the margin on every direct child of a table cell,
        // mirroring PlateEditor.module.scss `td/th > * { margin: 0 }`. A pasted
        // table's cell is a <p> inside the <td>; with the email client's default
        // <p> margin (~16px top+bottom) every cell renders uniformly taller than
        // in the editor. Email clients strip <style>, so this must be inline.
        root.querySelectorAll('td > *, th > *').forEach((child) => {
            const el = child as HTMLElement;
            if (!el.style.cssText.includes('margin')) {
                el.style.margin = '0';
            }
        });
    }

    let out = (root as HTMLElement).innerHTML;
    out = out.replace(ZERO_WIDTH_CHARS_REGEX, '');
    return out.trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export async function plateToHtml(value: Value, options: PlateToHtmlOptions = {}): Promise<string> {
    const nodes = (Array.isArray(value) ? value : []) as PlateNode[];
    const emailStyles = options.emailStyles === true;

    // Empty-body contract: a doc with no text and no media → ''.
    if (isEmptyDocument(nodes)) return '';

    // 1. Resolve image docIds → URLs.
    const docIds = collectImageDocIds(nodes);
    let urlMap: Record<string, string> = {};
    if (docIds.length > 0) {
        const resolver = options.resolveImageUrls ?? fetchImageUrlsBulk;
        try {
            urlMap = await resolver(docIds);
        } catch (err) {
            // Bulk resolve failed — images fall back to their docId src and
            // get dropped by the consumer if unresolvable; do not fail export.
            console.error('[plateToHtml] image URL resolution failed; sending unresolved docIds', err);
            urlMap = {};
        }
    }
    const resolved = resolveImageNodes(nodes, urlMap);

    // 2. Reconstruct indent-based lists into ul/ol/li element nodes.
    const withLists = reconstructLists(resolved, options.nestLists);

    // 3. Native static serialization.
    const editor = createStaticEditor({
        plugins: buildStaticPlugins(emailStyles),
        value: withLists as unknown as Value,
    } as any);
    const rawHtml = await serializeHtml(editor as any, {
        stripDataAttributes: true,
        stripClassNames: false,
        // Keep the mention class through serializeHtml's class stripper.
        preserveClassNames: [MENTION_CLASS],
    });

    // 4. Clean wrapper noise into portable HTML.
    return cleanSerializedHtml(rawHtml, emailStyles);
}
